import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Share2, Briefcase, MessageSquare, Megaphone, HandHeart, MoreHorizontal, Pencil, Trash2, Flag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { LikeButton } from './LikeButton';
import { CommentSection } from './CommentSection';
import { MediaGallery } from './MediaGallery';
import { LinkifiedText } from './LinkifiedText';
import { LinkPreview } from './LinkPreview';
import { EditPostDialog } from './EditPostDialog';
import { getFirstUrl } from '@/lib/urls';
import { useAuth } from '@/contexts/AuthContext';
import { useDeletePost } from '@/hooks/usePosts';
import { useToast } from '@/hooks/use-toast';
import { ReportDialog } from '@/components/ReportDialog';
import type { Database } from '@/integrations/supabase/types';

type PostCategory = Database['public']['Enums']['post_category'];

interface PostCardProps {
  post: {
    id: string;
    user_id: string;
    content: string;
    category: PostCategory;
    created_at: string;
    likes_count: number;
    comments_count: number;
    channel_id?: string | null;
    profiles: {
      id: string;
      full_name: string;
      avatar_url: string | null;
      company: string | null;
      role: string | null;
      headline: string | null;
    } | null;
    post_media?: {
      id: string;
      storage_path: string;
      file_name: string;
      file_type: string;
    }[];
  };
}

const CATEGORY_CONFIG: Record<PostCategory, { label: string; icon: React.ElementType; className: string }> = {
  referral: { label: 'Opportunity', icon: Briefcase, className: 'badge-referral' },
  discussion: { label: 'Discussion', icon: MessageSquare, className: 'badge-discussion' },
  plug: { label: 'Shameless Plug', icon: Megaphone, className: 'badge-plug' },
};

// --- Intro post parsing ---

// All known Heartbeat onboarding labels (both form versions) + new QBN onboarding
const INTRO_LABEL_MAP: Record<string, string> = {
  // Heartbeat v1 labels
  'Current Title (or desired title)': 'Title',
  'Current Company': 'Company',
  'What do you need help with?': 'Looking for',
  'What can you help with?': 'Can help with',
  'Primary Goal (i.e. job seeker, clients, referrals, networking)': 'Primary Goal',
  'Primary Field / Industry': 'Industry',
  'LinkedIn Profile URL': 'LinkedIn',
  'How did you hear about QBN?': 'Heard about QBN',
  'Looking For': 'Looking for',
  'Location': 'Location',
  // Heartbeat v2 labels
  'How do you see yourself contributing to the QBN community?': 'Contributing',
  'Primary Function': 'Primary Function',
  'What are you most interested in through QBN?': 'Interested in',
};

const ALL_LABELS = Object.keys(INTRO_LABEL_MAP);

interface IntroField {
  label: string;
  value: string;
}

function parseIntroContent(content: string): IntroField[] | null {
  // Quick check: does the content contain at least 2 known labels?
  const matchCount = ALL_LABELS.filter(label => content.includes(label)).length;
  if (matchCount < 2) return null;

  const fields: IntroField[] = [];

  // Strategy: find each label in the content, then extract the value text
  // that follows it (up to the next label or end of content).
  // First, find all label positions.
  const labelPositions: { label: string; start: number }[] = [];
  for (const label of ALL_LABELS) {
    let searchFrom = 0;
    while (true) {
      const idx = content.indexOf(label, searchFrom);
      if (idx === -1) break;
      // Make sure it's at start of a line (either start of string or preceded by newline)
      if (idx === 0 || content[idx - 1] === '\n') {
        labelPositions.push({ label, start: idx });
      }
      searchFrom = idx + label.length;
    }
  }

  // Sort by position in content
  labelPositions.sort((a, b) => a.start - b.start);

  for (let i = 0; i < labelPositions.length; i++) {
    const { label, start } = labelPositions[i];
    const valueStart = start + label.length;
    const valueEnd = i + 1 < labelPositions.length
      ? labelPositions[i + 1].start
      : content.length;

    const rawValue = content.substring(valueStart, valueEnd).trim();
    if (rawValue) {
      fields.push({
        label: INTRO_LABEL_MAP[label],
        value: rawValue,
      });
    }
  }

  return fields.length > 0 ? fields : null;
}

// New onboarding bio format with structured fields
const BIO_MARKERS = [
  { prefix: 'Current Title:', display: 'Title', isList: false },
  { prefix: 'Current Company:', display: 'Company', isList: false },
  { prefix: 'Primary Function:', display: 'Function', isList: false },
  { prefix: 'Contributing to QBN:', display: 'Contributing', isList: true },
  { prefix: 'Interested in through QBN:', display: 'Interested in', isList: true },
];

const INTRO_TAG_COLORS = [
  'bg-pink-500/15 border-pink-500/30 text-pink-600 dark:text-pink-300',
  'bg-teal-500/15 border-teal-500/30 text-teal-600 dark:text-teal-300',
  'bg-violet-500/15 border-violet-500/30 text-violet-600 dark:text-violet-300',
  'bg-amber-500/15 border-amber-500/30 text-amber-600 dark:text-amber-300',
  'bg-sky-500/15 border-sky-500/30 text-sky-600 dark:text-sky-300',
];

function parseNewBioContent(content: string): IntroField[] | null {
  const matchCount = BIO_MARKERS.filter(m => content.includes(m.prefix)).length;
  if (matchCount < 1) return null;

  const fields: IntroField[] = [];
  const paragraphs = content.split('\n\n');

  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) continue;
    const matched = BIO_MARKERS.find(m => trimmed.startsWith(m.prefix));
    if (matched) {
      const value = trimmed.slice(matched.prefix.length).trim();
      if (value) {
        fields.push({ label: matched.display, value });
      }
    } else if (trimmed.includes('linkedin.com/')) {
      fields.push({ label: 'LinkedIn', value: trimmed });
    } else {
      fields.push({ label: 'About', value: trimmed });
    }
  }

  return fields.length > 0 ? fields : null;
}

const LIST_LABELS = new Set(
  BIO_MARKERS.filter(m => m.isList).map(m => m.display)
);

function IntroPostContent({ fields }: { fields: IntroField[] }) {
  return (
    <div className="mb-4 space-y-3">
      {fields.map((field, i) => (
        <div key={i}>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            {field.label}
          </p>
          {field.label === 'LinkedIn' ? (
            <a
              href={field.value.startsWith('http') ? field.value : `https://${field.value}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline break-all"
            >
              {field.value}
            </a>
          ) : LIST_LABELS.has(field.label) ? (
            <div className="flex flex-wrap gap-1.5">
              {field.value.split(',').map((item, j) => {
                const trimmed = item.trim();
                if (!trimmed) return null;
                return (
                  <span
                    key={trimmed}
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium border ${INTRO_TAG_COLORS[j % INTRO_TAG_COLORS.length]}`}
                  >
                    {trimmed}
                  </span>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-foreground">{field.value}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// Collapse 3+ consecutive newlines down to a single blank line for regular posts
function cleanContent(content: string): string {
  return content.replace(/\n{3,}/g, '\n\n').trim();
}

export function PostCard({ post }: PostCardProps) {
  const { user } = useAuth();
  const deletePost = useDeletePost();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const profile = post.profiles;
  const categoryConfig = CATEGORY_CONFIG[post.category];
  const CategoryIcon = categoryConfig.icon;
  const introFields = parseNewBioContent(post.content) || parseIntroContent(post.content);
  const isIntroPost = !!introFields;
  const firstUrl = isIntroPost ? null : getFirstUrl(post.content);
  const isOwnPost = user?.id === post.user_id;

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await deletePost.mutateAsync(post.id);
      toast({ title: 'Post deleted' });
    } catch (error: any) {
      toast({ title: 'Error deleting post', description: error.message, variant: 'destructive' });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post by ${profile?.full_name || 'QBN Member'}`,
          text: post.content.substring(0, 100) + (post.content.length > 100 ? '...' : ''),
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <Card className="card-qbn">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex gap-3">
            <Link to={`/members/${post.user_id}`}>
              <Avatar className="h-11 w-11 cursor-pointer hover:opacity-80 transition-opacity">
                <SignedAvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback
                  className="text-white font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, hsl(340 82% 52%) 0%, hsl(255 100% 65%) 100%)',
                  }}
                >
                  {profile?.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div>
              <Link to={`/members/${post.user_id}`} className="hover:underline">
                <p className="text-[15px] font-semibold text-foreground">
                  {profile?.full_name || 'Unknown'}
                </p>
              </Link>
              <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isIntroPost ? (
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
                <HandHeart className="h-3 w-3 mr-1" />
                Intro
              </Badge>
            ) : (
              <Badge variant="outline" className={categoryConfig.className}>
                <CategoryIcon className="h-3 w-3 mr-1" />
                {categoryConfig.label}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isOwnPost && (
                  <>
                    <DropdownMenuItem onClick={() => setEditOpen(true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
                {!isOwnPost && (
                  <DropdownMenuItem onClick={() => setReportOpen(true)}>
                    <Flag className="h-4 w-4 mr-2" />
                    Report Post
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Content */}
        {isIntroPost ? (
          <IntroPostContent fields={introFields} />
        ) : (
          <p className="text-foreground text-[15px] leading-relaxed whitespace-pre-wrap mb-4">
            <LinkifiedText text={cleanContent(post.content)} />
          </p>
        )}

        {/* Link Preview */}
        {firstUrl && <LinkPreview url={firstUrl} />}

        {/* Media Gallery */}
        {post.post_media && post.post_media.length > 0 && (
          <div className="mb-4">
            <MediaGallery media={post.post_media} />
          </div>
        )}

        {/* Reactions */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
          <LikeButton postId={post.id} initialCount={post.likes_count} />
          <CommentSection postId={post.id} initialCount={post.comments_count} />
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 bg-secondary/5 border border-border rounded-[20px] px-3 py-1.5 text-muted-foreground text-[13px] hover:border-primary hover:text-primary transition-colors"
          >
            <Share2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardContent>

      {isOwnPost && (
        <EditPostDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          postId={post.id}
          initialContent={post.content}
          initialCategory={post.category}
        />
      )}

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        reportedPostId={post.id}
        reportedUserId={post.user_id}
        reportedName={profile?.full_name}
      />
    </Card>
  );
}
